// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import { Card, H2, LgPadding, ListBase, MdSpacing } from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import { useNavigation } from '@alto/navigation';
import React, { useCallback, useMemo } from 'react';
import { ASSISTANT_PRESSED_EVENTS } from '~shared/features/alto-assistant/analytics/constants';
import { ASSISTANT_CATEGORIES, type AssistantShortcut } from '~shared/features/alto-assistant/constants';
import { getCurrentUserID } from '~shared/features/users/selectors/getUsers';
import { useAnalytics, useIsFeatureDisabledForCompany } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useSelectorShared } from '~shared/store';
import { useQueryInstructionalVideos } from '../../../video/hooks/useQueryInstructionalVideos';
import { FAQ_KEYS } from '../faq/Constants';
import { MenuOption } from '../wizard/MenuOption';

export const Shortcuts = () => {
  const { navigate } = useNavigation();
  const { trackEvent } = useAnalytics();
  const { value: billingStormEnabled } = Experimentation.useFeatureFlag('billing_storm');
  const { videos } = useQueryInstructionalVideos();
  const userID = useSelectorShared(getCurrentUserID);
  const { isFeatureDisabled: shouldDisableTransferPrescription, isLoading: isFeatureDisablingLoading } =
    useIsFeatureDisabledForCompany('transfer_prescription');

  const trackShortcutPress = useCallback(
    (action: AssistantShortcut) => {
      trackEvent({
        event: EVENTS.ASSISTANT_COMPONENT_PRESSED,
        params: {
          name: ASSISTANT_PRESSED_EVENTS.SHORTCUT,
          action,
          origin: ORIGIN_NAMES.MESSAGES,
        },
      });
    },
    [trackEvent],
  );

  const shortcuts = useMemo(
    () =>
      [
        {
          key: 'edit-insurance',
          component: (
            <MenuOption
              iconName="idcard"
              text="Edit insurance"
              onPress={() => {
                trackShortcutPress('edit_insurance');
                if (!userID) return;
                navigate('RouteUserProfile', { userID });
              }}
            />
          ),
        },
        {
          key: 'price-investigation',
          component: (
            <MenuOption
              iconName="alert"
              text="My price isn't what I expected"
              onPress={() => {
                trackShortcutPress(FAQ_KEYS.PRICE_INVESTIGATION);
                navigate('RouteFaq', {
                  faqKey: FAQ_KEYS.PRICE_INVESTIGATION,
                });
              }}
            />
          ),
          show: billingStormEnabled,
        },
        {
          key: 'edit-an-upcoming-order',
          component: (
            <MenuOption
              iconName="box"
              text="Edit an upcoming order"
              onPress={() => {
                trackShortcutPress('edit_upcoming_delivery');
                navigate('RouteMessagesDeliverySelection', {
                  origin: ORIGIN_NAMES.MESSAGES,
                  category: ASSISTANT_CATEGORIES.DELIVERIES,
                });
              }}
            />
          ),
        },
        {
          key: 'transfer-prescription-to-alto',
          component: (
            <MenuOption
              iconName="swap"
              text="Transfer prescription to Alto"
              onPress={() => {
                trackShortcutPress('transfer_in');
                navigate('RouteTransferPrescription', { origin: ORIGIN_NAMES.MESSAGES });
              }}
            />
          ),
          show: !isFeatureDisablingLoading && !shouldDisableTransferPrescription,
        },
        {
          key: 'injection-guides',
          component: (
            <MenuOption
              iconName="play"
              text="Injection guides"
              onPress={() => {
                trackShortcutPress('injection_guides');
                navigate('RouteInstructionalVideos');
              }}
            />
          ),
          show: (videos?.length ?? 0) > 0,
        },
        {
          key: 'download-receipts',
          component: (
            <MenuOption
              iconName="receipt"
              text="Download receipts"
              onPress={() => {
                trackShortcutPress('download_receipts');
                navigate('RoutePayments');
              }}
            />
          ),
        },
      ].filter((shortcut) => shortcut.show !== false),
    [
      billingStormEnabled,
      navigate,
      trackShortcutPress,
      userID,
      videos?.length,
      isFeatureDisablingLoading,
      shouldDisableTransferPrescription,
    ],
  );

  return (
    <Card>
      <MdSpacing />
      <LgPadding
        topPadding={SPACING.STATIC.NONE}
        bottomPadding={SPACING.STATIC.NONE}
      >
        <H2>Shortcuts</H2>
      </LgPadding>
      <MdSpacing />
      <ListBase items={shortcuts} />
    </Card>
  );
};
